// ═══════════════════════════════════════════════════════════════════
//  BOOK RENDER — shared helpers for Manuals / Policies / SOPs.
//
//  Produces true-book layout on screen and in print:
//    • Cover page: circle drop-cap with first letter + ❦ ✦ ❦ ornament
//    • Auto chapter numbering (Chapter 1, Chapter 2…)
//    • Section numbering (1.1, 1.2, 1.2.1…)
//    • Table of Contents with dot-leaders + real page numbers
//    • Running header = current chapter (via CSS string-set + paged.js)
//    • Page numbers bottom-centre on every page except cover
// ═══════════════════════════════════════════════════════════════════

// ── Screen-mode CSS (book typography + ornaments) ──
export const BOOK_CSS = `
.book-view { font-family: 'Georgia','Iowan Old Style','Palatino Linotype',serif; color:#1e293b; font-size:15.5px; line-height:1.85; text-align:justify; hyphens:auto; }
.book-view p { margin:0 0 12px; text-indent:1.6em; }
.book-view p:first-of-type, .book-view h1+p, .book-view h2+p, .book-view h3+p { text-indent:0; }
.book-view h1 { font-size:1.9rem; margin:32px 0 14px; text-align:center; letter-spacing:0.5px; color:#0f172a; position:relative; }
.book-view h1::before { content:""; display:block; border-top:2px solid #cbd5e1; width:60%; margin:0 auto 14px; }
.book-view h1::after  { content:"❦ ✦ ❦"; display:block; text-align:center; color:#94a3b8; letter-spacing:12px; font-size:1rem; margin-top:8px; }
.book-view h2 { font-size:1.3rem; margin:26px 0 10px; padding-bottom:6px; border-bottom:1px solid #e2e8f0; color:#0f172a; }
.book-view h3 { font-size:1.08rem; margin:18px 0 6px; color:#334155; font-weight:700; }
.book-view ul, .book-view ol { margin:0 0 14px; padding-left:2em; }
.book-view li { margin-bottom:4px; }
.book-view table { border-collapse:collapse; margin:14px auto; font-size:14px; min-width:60%; box-shadow:0 0 0 1px #cbd5e1; }
.book-view td, .book-view th { border:1px solid #cbd5e1; padding:7px 11px; }
.book-view th { background:#f8fafc; font-weight:700; }
.book-view b, .book-view strong { color:#0f172a; }
.book-view blockquote { border-left:3px solid #cbd5e1; margin:14px 0; padding:4px 16px; color:#475569; font-style:italic; }

.book-cover { text-align:center; padding:40px 20px 50px; border-bottom:2px solid #e2e8f0; margin-bottom:30px; }
.book-cover .drop-letter {
  width:120px; height:120px; border-radius:50%;
  border:3px solid #0f172a;
  display:flex; align-items:center; justify-content:center;
  font-size:60px; font-weight:700; color:#0f172a;
  font-family:'Georgia',serif;
  margin:0 auto 18px;
  background:#fff;
  box-shadow:0 4px 16px rgba(15,23,42,0.08);
}
.book-cover .ornament { color:#94a3b8; letter-spacing:16px; font-size:1.1rem; margin:10px 0 16px; }
.book-cover .book-title { font-size:2rem; margin:0 0 6px; font-weight:700; color:#0f172a; letter-spacing:0.6px; }
.book-cover .book-sub { color:#64748b; font-size:13px; font-style:italic; }

.book-index {
  background:#fafaf7;
  border:1px solid #e2e8f0;
  border-radius:6px;
  padding:22px 28px;
  margin:0 0 28px;
  max-width:720px;
  margin-left:auto; margin-right:auto;
}
.book-index .idx-title {
  text-align:center; font-size:1.3rem; font-weight:700; letter-spacing:2px;
  margin:0 0 14px; padding-bottom:8px; border-bottom:2px solid #cbd5e1;
  color:#0f172a;
}
.book-index .idx-item {
  display:flex; align-items:baseline; gap:6px;
  padding:3px 0; font-size:13.5px; line-height:1.5;
  cursor:pointer;
}
.book-index .idx-item:hover .idx-name { color:#0369a1; }
.book-index .idx-item .idx-name { color:#0f172a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.book-index .idx-item .idx-dots { flex:1; border-bottom:1px dotted #94a3b8; transform:translateY(-4px); min-width:14px; }
.book-index .idx-item .idx-num { color:#475569; font-variant-numeric:tabular-nums; font-weight:600; }
.book-index .idx-item.l1 { font-weight:700; margin-top:8px; font-size:14px; }
.book-index .idx-item.l2 { padding-left:16px; }
.book-index .idx-item.l3 { padding-left:32px; font-size:12.5px; color:#475569; }

.book-view .chapter-no {
  display:block; font-size:0.7em; color:#64748b; letter-spacing:3px;
  text-transform:uppercase; font-weight:600; margin-bottom:4px;
}
`;

// ── Substitute {{clinicName}} / {{clinicShort}} with live values ──
export function substituteClinic(text, clinic) {
  if (!text) return text;
  const name  = (clinic && clinic.clinicName)  || 'Your Clinic';
  const short = (clinic && clinic.clinicShort) || 'CL';
  return String(text)
    .replace(/\{\{\s*clinicName\s*\}\}/g, name)
    .replace(/\{\{\s*clinicShort\s*\}\}/g, short);
}

// ── Extract headings → returns [{level, text, id, num}] ──
export function extractTOC(html) {
  if (!html) return [];
  const toc = [];
  const rx = /<(h[123])[^>]*>([\s\S]*?)<\/\1>/gi;
  let m, idx = 0;
  while ((m = rx.exec(html)) !== null) {
    const level = Number(m[1].slice(1));
    const text = m[2].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (text) toc.push({ level, text, id: `h-${idx++}` });
  }
  // Auto-numbering
  let c1 = 0, c2 = 0, c3 = 0;
  toc.forEach(t => {
    if (t.level === 1) { c1++; c2 = 0; c3 = 0; t.num = `${c1}`; }
    else if (t.level === 2) { c2++; c3 = 0; t.num = `${Math.max(c1,1)}.${c2}`; }
    else { c3++; t.num = `${Math.max(c1,1)}.${Math.max(c2,1)}.${c3}`; }
  });
  return toc;
}

// ── Add id="h-N" anchors (for TOC jump links) ──
export function addHeadingIds(html) {
  if (!html) return html;
  let idx = 0;
  return html.replace(/<(h[123])>/gi, (_, tag) => `<${tag} id="h-${idx++}">`);
}

// ── Auto-number chapters on screen ──
export function numberChapters(html) {
  if (!html) return html;
  let idx = 0, c1 = 0, c2 = 0, c3 = 0;
  return html
    .replace(/<(h[123])>([\s\S]*?)<\/\1>/gi, (_, tag, inner) => {
      const id = `h-${idx++}`;
      const level = Number(tag.slice(1));
      if (level === 1) { c1++; c2 = 0; c3 = 0; return `<h1 id="${id}"><span class="chapter-no">Chapter ${c1}</span>${inner}</h1>`; }
      if (level === 2) { c2++; c3 = 0; return `<h2 id="${id}"><span style="color:#64748b;font-weight:400;margin-right:8px">${Math.max(c1,1)}.${c2}</span>${inner}</h2>`; }
      c3++; return `<h3 id="${id}"><span style="color:#94a3b8;font-weight:400;margin-right:6px">${Math.max(c1,1)}.${Math.max(c2,1)}.${c3}</span>${inner}</h3>`;
    });
}

// ── Index block (TOC for screen) ──
export function renderIndex(toc, onJump) {
  // Returns JSX? No — return HTML string for parity. We handle interactive version in React.
  // Helper only — used in print.
  if (!toc.length) return '';
  return `<div class="book-index"><div class="idx-title">INDEX</div>${
    toc.map(t => `<div class="idx-item l${t.level}"><span class="idx-name">${t.num} &nbsp; ${t.text}</span><span class="idx-dots"></span><span class="idx-num page-num" data-target="${t.id}">—</span></div>`).join('')
  }</div>`;
}

// ── Full book-format PRINT window HTML (with paged.js) ──
export function buildBookPrintHtml(doc, clinic = null) {
  const clinicName = (clinic && clinic.clinicName) || 'Your Clinic';
  const liveTitle = substituteClinic(doc.title || '', clinic);
  const liveContent = substituteClinic(doc.content || '', clinic);
  const liveCode = substituteClinic(doc.code || '', clinic);
  const withIds = addHeadingIds(liveContent);
  const toc = extractTOC(withIds);
  const firstLetter = liveTitle.trim().charAt(0).toUpperCase() || '?';

  const tocRows = toc.map(t => `
    <li class="tl${t.level}">
      <a href="#${t.id}" class="tname"><span class="tnum">${t.num}</span>${escapeHtml(t.text)}</a>
      <span class="tleader"></span>
      <span class="tpage"></span>
    </li>`).join('');

  // Auto-number chapters in the content for print
  let c1 = 0, c2 = 0, c3 = 0, idx = 0;
  const numberedContent = liveContent.replace(/<(h[123])>([\s\S]*?)<\/\1>/gi, (_, tag, inner) => {
    const id = `h-${idx++}`;
    const lvl = Number(tag.slice(1));
    if (lvl === 1) { c1++; c2 = 0; c3 = 0;
      return `<h1 id="${id}"><span class="chapter-label">Chapter ${c1}</span>${inner}</h1>`; }
    if (lvl === 2) { c2++; c3 = 0;
      return `<h2 id="${id}"><span class="section-num">${Math.max(c1,1)}.${c2}</span>${inner}</h2>`; }
    c3++;
    return `<h3 id="${id}"><span class="subsection-num">${Math.max(c1,1)}.${Math.max(c2,1)}.${c3}</span>${inner}</h3>`;
  });

  return `<!doctype html><html><head><meta charset="utf-8"/><title>${escapeHtml(liveTitle)}</title>
<style>
  /* ─── Paged.js page setup ─── */
  @page {
    size: A4;
    margin: 22mm 20mm 22mm 22mm;
    @bottom-center {
      content: counter(page);
      font-family: Georgia, serif; font-size: 10pt; color: #475569;
    }
    @top-center {
      content: string(current-chapter);
      font-family: Georgia, serif; font-size: 9pt; color: #64748b;
      letter-spacing: 1.2px; text-transform: uppercase;
    }
  }
  @page cover { @top-center { content: ""; } @bottom-center { content: ""; } }
  @page toc   { @top-center { content: "CONTENTS"; } }

  /* Cover page */
  .cover { page: cover; page-break-after: always; text-align: center; padding-top: 36mm; }
  .cover .drop {
    width: 46mm; height: 46mm; border: 2.2mm solid #0f172a; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 32mm; font-weight: 700; font-family: Georgia, serif; color: #0f172a;
    margin: 0 auto 10mm;
  }
  .cover .ornament { font-size: 7mm; color: #94a3b8; letter-spacing: 5mm; margin: 4mm 0 12mm; }
  .cover .title { font-size: 11mm; font-weight: 700; letter-spacing: 1px; margin: 0 0 4mm; color:#0f172a; }
  .cover .subtitle { font-size: 5mm; color: #475569; font-style: italic; margin: 0 0 24mm; }
  .cover .clinic { font-size: 4.5mm; color: #0f172a; font-weight: 600; letter-spacing: 0.5mm; }
  .cover .meta { font-size: 3.6mm; color: #64748b; margin-top: 6mm; }
  .cover .rule { width: 50mm; border: 0; border-top: 0.4mm solid #94a3b8; margin: 8mm auto; }

  /* TOC page */
  .toc { page: toc; page-break-after: always; padding-top: 6mm; }
  .toc h1.toc-title {
    text-align: center; font-size: 10mm; font-weight: 700; letter-spacing: 5mm;
    margin: 0 0 10mm; padding-bottom: 3mm; border-bottom: 0.6mm solid #cbd5e1;
    color:#0f172a;
  }
  .toc ul { list-style: none; padding: 0; margin: 0; }
  .toc li { display: flex; align-items: baseline; gap: 2mm; font-size: 4mm; line-height: 1.7; margin: 1.2mm 0; }
  .toc li.tl1 { font-weight: 700; font-size: 4.4mm; margin-top: 4mm; }
  .toc li.tl2 { padding-left: 6mm; }
  .toc li.tl3 { padding-left: 12mm; font-size: 3.6mm; color: #475569; }
  .toc .tname { flex-shrink: 0; color: #0f172a; text-decoration: none; }
  .toc .tnum  { display: inline-block; min-width: 12mm; color:#64748b; font-weight:600; margin-right: 3mm; }
  .toc .tleader { flex: 1; border-bottom: 0.2mm dotted #94a3b8; transform: translateY(-1mm); margin: 0 2mm; }
  .toc .tpage { flex-shrink: 0; color: #475569; font-variant-numeric: tabular-nums; font-weight:600; }
  /* Paged.js will populate .tpage via target-counter */
  .toc a[href]::after { content: target-counter(attr(href), page); }
  .toc li .tpage::before { content: ""; }

  /* Content */
  body { font-family: 'Georgia','Iowan Old Style',serif; color:#1e293b; font-size: 11pt; line-height: 1.75; margin: 0; }
  .content { text-align: justify; hyphens: auto; }
  .content h1 {
    page-break-before: always;
    string-set: current-chapter attr(data-chapter-plain, "");
    text-align: center; font-size: 1.9rem; margin: 16mm 0 8mm;
    color:#0f172a;
  }
  .content h1 .chapter-label {
    display: block; font-size: 0.55em; color: #64748b; letter-spacing: 3px;
    text-transform: uppercase; font-weight: 600; margin-bottom: 3mm;
  }
  .content h1::before {
    content: "";
    display: block; border-top: 0.4mm solid #94a3b8; width: 30mm; margin: 0 auto 6mm;
  }
  .content h1::after {
    content: "❦ ✦ ❦";
    display: block; text-align: center; color: #94a3b8; letter-spacing: 4mm; font-size: 0.5em; margin-top: 4mm;
  }
  .content h2 { font-size: 1.25rem; margin: 10mm 0 4mm; padding-bottom: 1.5mm; border-bottom: 0.2mm solid #cbd5e1; page-break-after: avoid; }
  .content h2 .section-num { color: #64748b; font-weight: 400; margin-right: 3mm; }
  .content h3 { font-size: 1.05rem; margin: 6mm 0 2mm; color: #334155; page-break-after: avoid; }
  .content h3 .subsection-num { color: #94a3b8; font-weight: 400; margin-right: 2mm; }
  .content p { margin: 0 0 3mm; text-indent: 5mm; }
  .content h1+p, .content h2+p, .content h3+p { text-indent: 0; }
  .content ul, .content ol { padding-left: 6mm; margin: 2mm 0 4mm; }
  .content li { margin-bottom: 1mm; }
  .content table { border-collapse: collapse; margin: 4mm auto; font-size: 10pt; }
  .content td, .content th { border: 0.2mm solid #cbd5e1; padding: 2mm 3mm; }
  .content th { background: #f8fafc; font-weight: 700; }
  .content blockquote { border-left: 0.6mm solid #cbd5e1; margin: 4mm 0; padding: 1mm 4mm; color: #475569; font-style: italic; }
  .content a { color: inherit; text-decoration: none; }

  /* Chapter running-header string: inject data-chapter-plain at runtime via JS below */
</style></head><body>
  <div class="cover">
    <div class="drop">${escapeHtml(firstLetter)}</div>
    <div class="ornament">❦ &nbsp; ✦ &nbsp; ❦</div>
    <h1 class="title">${escapeHtml(liveTitle)}</h1>
    ${liveCode ? `<div class="subtitle">${escapeHtml(liveCode)}</div>` : ''}
    <hr class="rule" />
    <div class="clinic">${escapeHtml(clinicName)}</div>
    <div class="meta">Version ${doc.version || 1} &nbsp;·&nbsp; ${doc.lastEditedAt ? new Date(doc.lastEditedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' }) : ''}</div>
    ${doc.lastEditedBy ? `<div class="meta">Edited by ${escapeHtml(doc.lastEditedBy)}</div>` : ''}
  </div>

  <div class="toc">
    <h1 class="toc-title">INDEX</h1>
    <ul>${tocRows}</ul>
  </div>

  <div class="content">${numberedContent}</div>

  <script>
    // Inject running-header text on each H1 (plain text, no HTML)
    document.querySelectorAll('.content h1').forEach(h1 => {
      const clone = h1.cloneNode(true);
      clone.querySelectorAll('.chapter-label').forEach(n => n.remove());
      h1.setAttribute('data-chapter-plain', clone.textContent.trim());
    });
  </script>
  <script src="https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js"></script>
  <script>
    // After paged.js finishes, open print dialog
    class PrintHandler extends Paged.Handler {
      afterRendered() { setTimeout(() => window.print(), 300); }
    }
    Paged.registerHandlers(PrintHandler);
  </script>
</body></html>`;
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, ch => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[ch]));
}
